// Five-star ratings, drawn here rather than pulled from an icon font — the
// panel's other glyphs are hand-drawn SVG (see PlaceIcons.jsx) and a stray
// emoji star renders differently on every platform.
//
// One row, always: stars then a short label. Pass `onRate` and the stars become
// buttons; leave it off and it's a display. The fill is a second copy of the
// row clipped to a percentage width, which is what gives a true 3.4-star fill
// without per-star maths.
//
// An unrated place is the default everywhere: five empty stars and "No rating".
// Copy-out clean: no imports outside src/pathfinder.

import { useState } from 'react';

const STARS = [1, 2, 3, 4, 5];

// A five-point star on a 24x24 grid, points touching the edges.
const STAR_PATH = 'M12 3.1l2.75 5.57 6.15.9-4.45 4.33 1.05 6.12L12 17.13l-5.5 2.89 1.05-6.12L3.1 9.57l6.15-.9z';

function Row({ size, filled }) {
  return (
    <span className={`pfa-stars-row${filled ? ' filled' : ''}`}>
      {STARS.map(n => (
        <svg key={n} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d={STAR_PATH}
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={filled ? 0 : 1.7}
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

/**
 * `value` — the average, 0 when nobody has rated (0 stars filled).
 * `count` — how many ratings that average is built from.
 * `mine`  — this browser's own rating, previewed in place of the average.
 * `onRate(stars)` — present makes the row tappable; omit for display only.
 */
export default function StarRating({
  value = 0, count = 0, size = 14, onRate = null, mine = null, busy = false,
}) {
  const [hover, setHover] = useState(0);
  // While rating, the row shows what you picked; otherwise the average.
  const shown = hover || (onRate && mine ? mine : value) || 0;
  const pct = Math.max(0, Math.min(100, (shown / 5) * 100));

  const label = count > 0
    ? `${value.toFixed(1)} (${count})`
    : (busy ? 'Saving…' : 'No rating');

  const stars = (
    <span className="pfa-stars" style={{ '--pfa-star-pct': `${pct}%` }}>
      <Row size={size} filled={false} />
      {/* Clipped overlay — width is the score, so 3.4 fills 68% of the row. */}
      <span className="pfa-stars-fill"><Row size={size} filled /></span>
    </span>
  );

  if (!onRate) {
    return (
      <span className="pfa-rating">
        {stars}
        <span className="pfa-rating-text">{label}</span>
      </span>
    );
  }

  return (
    <span className={`pfa-rating interactive${busy ? ' busy' : ''}`}>
      <span className="pfa-stars-input" onMouseLeave={() => setHover(0)}>
        {stars}
        {/* Real buttons over the drawn row: keyboard-reachable, and each one a
            full-height target rather than a 14px glyph. */}
        {STARS.map(n => (
          <button
            key={n}
            type="button"
            className="pfa-star-hit"
            style={{ left: `${(n - 1) * 20}%` }}
            disabled={busy}
            onMouseEnter={() => setHover(n)}
            onFocus={() => setHover(n)}
            onBlur={() => setHover(0)}
            onClick={() => onRate(n)}
            aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
            aria-pressed={mine === n}
          />
        ))}
      </span>
      <span className="pfa-rating-text">
        {label}{mine ? ' · you' : ''}
      </span>
    </span>
  );
}
